import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from './redux/actions';
import * as commonActions from '../common/redux/actions';
import CollectionHeaderNav from './CollectionHeaderNav';
import CustomLoader from '../common/CustomLoader';
import types from '../../common/formElements/CollectionSectionType';
import { drapcodeIcon, iconForField } from '../../common/custom/icon-library';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import CustomSelect from '../home/collections/CustomSelect';
import { ListMappings } from './collection/collection-mapping/ListMappings';
import { isProjectReadOnly } from '../../common/utils/api_utils';

export class CollectionMapping extends Component {
  static propTypes = {
    collections: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    commonActions: PropTypes.object.isRequired,
  };

  state = {
    targetCollection: '',
    targetCollectionFields: [{ value: '', key: '' }],
    referenceFields: [],
    referenceCollectionFields: [{ value: '', key: '' }],
  };

  componentDidMount() {
    const { match, actions } = this.props;
    const { params } = match;
    const { projectId, collectionId, version } = params;
    const { loadProjectCollectionDetail, loadProjectCollections } = actions;
    loadProjectCollectionDetail({ projectId, collectionId, version });
    loadProjectCollections({ projectId, version });
  }

  handleChange = (name, value) => {
    this.setState({ [name]: value });
    if (name[0] === 'targetCollection' && value) {
      this.fetchCollectionDetails(value.value);
    }
  };

  fetchCollectionDetails = (collectionId) => {
    const { collections } = this.props;
    const { projectCollections } = collections;
    try {
      const targetCollection = projectCollections.find(
        (collection) => collection.uuid === collectionId,
      );
      const targetCollectionFields = targetCollection.fields
        .filter((field) => field.fieldName !== 'primary_key')
        .map((field) => ({
          value: field.fieldName,
          label: field.fieldTitle.en,
        }));
      this.setState({ targetCollectionFields });
      const referenceFields = targetCollection.fields.filter((field) => field.type === 'reference');
      this.setState({ referenceFields });
    } catch (error) {
      console.error('Error fetching collection details:', error);
    }
  };

  handleFieldChange = (name, value) => {
    const refFieldDetails = this.state.referenceFields.find(
      (refField) => refField.fieldName === value.value,
    );
    if (
      refFieldDetails &&
      refFieldDetails.refCollection &&
      refFieldDetails.refCollection.collectionName
    ) {
      this.fetchReferenceCollectionDetails(refFieldDetails.refCollection.collectionName);
    }
  };

  fetchReferenceCollectionDetails = (collectionName) => {
    const { collections } = this.props;
    const { projectCollections } = collections;
    try {
      const referenceCollection = projectCollections.find(
        (collection) => collection.collectionName === collectionName,
      );
      const referenceCollectionFields = referenceCollection.fields
        .filter((field) => field.fieldName !== 'primary_key')
        .map((field) => ({
          value: field.fieldName,
          label: field.fieldTitle.en,
        }));
      this.setState({ referenceCollectionFields });
    } catch (error) {
      console.error('Error fetching reference collection details:', error);
    }
  };

  handleSubmit = async (values, setSubmitting, resetForm) => {
    const { collections, match, actions, commonActions } = this.props;
    const { projectId, version } = match.params;
    const { selectedCollection } = collections;
    setSubmitting(true);
    const mappings = selectedCollection.fields
      .map((field) => {
        const targetFieldValue = values[field.fieldName];
        if (targetFieldValue) {
          const mapping = {
            metaDataField: field.fieldName,
            targetCollectionField: targetFieldValue.value,
          };
          const refFieldDetails = this.state.referenceFields.find(
            (refField) => refField.fieldName === targetFieldValue.value,
          );
          if (refFieldDetails) {
            mapping.referenceCollectionField = values[`${field.fieldName}_refField`].value || '';
            mapping.referenceCollectionName = refFieldDetails.refCollection.collectionName || '';
          }
          return mapping;
        }
        return null;
      })
      .filter(Boolean);
    const data = {
      mappingName: values.mappingName,
      targetCollection: values.targetCollection.value || '',
      mappings: mappings,
      primaryKey: 'uuid',
    };
    try {
      await actions.createMetaDataMappingInCollection({
        projectId,
        version,
        data,
        selectedCollection,
      });
      commonActions.trackCollectionChange({
        uuid: selectedCollection.uuid,
        name: selectedCollection.collectionName,
      });

      resetForm();
      this.setState({ targetCollection: '' });
    } catch (error) {
      console.error('Error creating mapping:', error);
    } finally {
      setSubmitting(false);
    }
  };

  render() {
    const { collections, match, actions, commonActions } = this.props;
    const { projectId, version } = match.params;
    const { loadProjectCollectionDetailPending, selectedCollection, projectCollections } =
      collections;
    const collectionOptions = selectedCollection
      ? projectCollections
          .filter((collection) => collection.uuid !== selectedCollection.uuid)
          .map((collection) => ({
            value: collection.uuid,
            label: collection.collectionTitle,
          }))
      : [];
    const isReadOnly = isProjectReadOnly(projectId);
    return (
      <div className="main collections-mapping">
        <div className="section">
          <div className="container">
            <CollectionHeaderNav
              label={types.COLLECTION_MAPPING}
              projectId={projectId}
              version={version}
              selectedCollection={selectedCollection}
              loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
            />
            {loadProjectCollectionDetailPending && (
              <div className="text-center">
                <CustomLoader
                  loading={loadProjectCollectionDetailPending}
                  size="20px"
                  color="#303453"
                />
              </div>
            )}
            {selectedCollection && (
              <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 drapcode">
                <div className="row mx-auto">
                  <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 drapcode">
                    <div className="card">
                      <div
                        className="card-header card-header-icon"
                        data-background-color="drapcode-dark-blue"
                      >
                        <i className={`wb ${drapcodeIcon('collectionMapping')}`}></i>
                      </div>
                      <div className="card-content">
                        <h5 className="card-title mb-0 pb-0">
                          Add Collection Mapping{' '}
                          {typeof selectedCollection !== 'undefined' && selectedCollection
                            ? ' To ' + selectedCollection.collectionTitle
                            : ''}
                        </h5>
                        <p>
                          Add mapping to ensure that the mapped data gets saved in a collection.
                        </p>
                        <Formik
                          initialValues={{
                            mappingName: '',
                            targetCollection: '',
                            mappings: {},
                          }}
                          onSubmit={(values, { setSubmitting, resetForm }) => {
                            !isReadOnly && this.handleSubmit(values, setSubmitting, resetForm);
                          }}
                        >
                          {({ values, setFieldValue, setFieldTouched }) => (
                            <Form className="form-horizontal">
                              <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                                <div className="form-group">
                                  <div className="input-group">
                                    <div className="input-group-prepend" title="Mapping Name">
                                      <span className="input-group-text">
                                        <i className={`wb ${iconForField('text')}`}></i>
                                        <span className="required"> *</span>
                                      </span>
                                    </div>
                                    <Field
                                      type="text"
                                      name="mappingName"
                                      className="form-control form-control-sm"
                                      placeholder="Name of Collection Mapping"
                                    />
                                    <ErrorMessage name="mappingName">
                                      {(msg) => <p className="text-error">{msg}</p>}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                                <div className="form-group">
                                  <div className="input-group">
                                    <div
                                      className="input-group-prepend"
                                      title="Select Collection of which you want to map the data"
                                    >
                                      <span className="input-group-text">
                                        <i className={`wb ${iconForField('select')}`}></i>
                                        <span className="required"> *</span>
                                      </span>
                                    </div>
                                    <CustomSelect
                                      name="targetCollection"
                                      isMulti={false}
                                      value={this.state.targetCollection}
                                      options={collectionOptions}
                                      onChange={setFieldValue}
                                      onBlur={setFieldTouched}
                                      onChangeValue={this.handleChange}
                                      placeholder="Select Collection with which you want to map the data"
                                    />
                                    <ErrorMessage name="targetCollection">
                                      {(msg) => <p className="text-error">{msg}</p>}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                              {this.state.targetCollection && (
                                <div className="form-group">
                                  <div className="row">
                                    <div className="col-6">
                                      <h5>Meta Data Table Fields</h5>
                                    </div>
                                    <div className="col-6">
                                      <h5>Target Collection Fields</h5>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="row mx-auto mb-3">
                                      <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                                        <span className="input-group-text">
                                          <strong>Primary Key</strong>
                                        </span>
                                      </div>
                                      <div className="col-md-6 col-lg-6">
                                        <strong>Id</strong>
                                      </div>
                                    </div>
                                    {selectedCollection.fields
                                      .filter((field) => field.fieldName !== 'primary_key')
                                      .map((field) => (
                                        <div key={field.fieldName} className="row mx-auto mb-3">
                                          <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                                            <span className="input-group-text">
                                              {field.fieldTitle.en}
                                            </span>
                                          </div>
                                          <div className="col-md-6 col-lg-6">
                                            <CustomSelect
                                              name={field.fieldName}
                                              isMulti={false}
                                              value={values[field.fieldName]}
                                              options={this.state.targetCollectionFields}
                                              onChange={setFieldValue}
                                              onBlur={setFieldTouched}
                                              placeholder="Select Field"
                                              onChangeValue={this.handleFieldChange}
                                            />
                                          </div>
                                          {values[field.fieldName] &&
                                            this.state.referenceFields.some((refField) => {
                                              return (
                                                refField.fieldName === values[field.fieldName].value
                                              );
                                            }) && (
                                              <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                                                <CustomSelect
                                                  name={`${field.fieldName}_refField`}
                                                  isMulti={false}
                                                  value={values[`${field.fieldName}_refField`]}
                                                  options={this.state.referenceCollectionFields}
                                                  onChange={setFieldValue}
                                                  onBlur={setFieldTouched}
                                                  placeholder="Select Reference Collection Field"
                                                />
                                              </div>
                                            )}
                                        </div>
                                      ))}
                                  </div>
                                  <div className="text-right mt-3">
                                    <button
                                      type="submit"
                                      className="btn btn-theme-submit btn-md"
                                      disabled={isReadOnly}
                                      title={isReadOnly && 'Read-Only Move'}
                                    >
                                      <i className="wb webconnect-plus"></i>&nbsp;Save Mapping&nbsp;
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Form>
                          )}
                        </Formik>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 drapcode">
                    <div className="card">
                      <div
                        className="card-header card-header-icon"
                        data-background-color="drapcode-dark-blue"
                      >
                        <i className={`wb ${drapcodeIcon('list')}`}></i>
                      </div>
                      <div className="card-content">
                        <h5 className="card-title">Collection Mappings</h5>
                        <ListMappings
                          collections={collections}
                          actions={actions}
                          commonActions={commonActions}
                          projectId={projectId}
                          version={version}
                          isReadOnly={isReadOnly}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    collections: state.collections,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    commonActions: bindActionCreators({ ...commonActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CollectionMapping);
